import { useState } from 'react'

import { get } from 'lodash'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'
import { useMutation, useQuery } from 'react-query'
import * as yup from 'yup'

import { ManageBudgetForm } from './components'
import { FormHandler } from 'components'
import { API } from 'core/requests'
import {
  BudgetNewEmployeesBudgetType,
  BudgetPeriod,
  BudgetProrateType,
  BudgetStatus,
  BudgetYearType,
} from 'enums/budget'
import { t } from 'i18n'
import { BudgetType } from 'types'

const validationSchema = yup.object({
  period: yup
    .string()
    .required(t('budget.manageBudget.validations.periodRequired')),
  fiscal_year_start_month: yup
    .number()
    .nullable()
    .when('year_type', {
      is: BudgetYearType.Fiscal,
      then: yup
        .number()
        .nullable()
        .min(1, t('budget.manageBudget.validations.fiscalYearStartMonth.invalid'))
        .max(12, t('budget.manageBudget.validations.fiscalYearStartMonth.invalid'))
        .required(t('budget.manageBudget.validations.fiscalYearStartMonth.required')),
    }),
})

export default function ManageBudget({
  dialog,
  closeDialog,
  budgetSettings,
  fetchBudgetSettings,
}: any) {
  const intl = useIntl()

  const { enqueueSnackbar } = useSnackbar()
  const [isDisabled, setIsDisabled] = useState(true);

  // const initialValues: BudgetType = dialog?.data    //old condition for initialValues
  //   ? dialog?.data
  const initialValues: BudgetType = budgetSettings?.status === BudgetStatus.BudgetEnabled    //change condition for new design
    ? budgetSettings
    : {
      has_spending_limit: false,
      has_receiving_limit: true, //set default vlu (true) for Receiving Based Limit checked
      new_employees_budget_type: BudgetNewEmployeesBudgetType.Manual, //change new_employees_budget_type type Automatic to Manual
      new_employees_base_amount: 0,//set default new_employees_base_amount 0
      // A calendar year is the January-anchored case; the API derives the
      // concrete start/end dates from the anchor, so they are not sent.
      year_type: BudgetYearType.Calendar,
      fiscal_year_start_month: 1,
      period: BudgetPeriod.Quarterly,
      has_prorate: false,
      prorate_type: BudgetProrateType.NoBudgetInPeriod,
      enforce_manager_sending_limit: false,
      enforce_employee_receiving_limit: false,
      spending: {
        distribution: '',
        amount: null,
        rollover_per_period: false,
        rollover_annually: false,
      },
      receiving: {
        distribution: 'flat',  //set default vlu (flat/demographics) for RECEIVING RULES checked
        amount: null,
        rollover_per_period: false,
        rollover_annually: false,
      },
    }

  const [spendingBudgetPerEmployee, setSpendingBudgetPerEmployee] =
    useState(null)
  const [receivingBudgetPerEmployee, setReceivingBudgetPerEmployee] =
    useState(null)

  const { data: demographics, isLoading: demographicsIsLoading } = useQuery(
    'getDemographics',
    () => API.get('demographics'),
  )

  // const { data: roles, isLoading: rolesIsLoading } = useQuery('getRoles', () =>
  //   API.get('roles'),
  // )

  const updateBudgetSettingsMutation = useMutation(
    (payload: any) => API.update('updateBudgetSettings', payload),
    {
      onSuccess: (response: any, payload: any) => {
        if (response.status) {
          fetchBudgetSettings()
          // closeDialog()  //no need to close dialog

          // Saving the form returns it to its read-only state. Set the flag
          // rather than toggling it: the two switches above the form submit
          // through this same mutation and must leave the edit state alone.
          if (payload?.lockAfterSave) {
            setIsDisabled(true)
          }

          enqueueSnackbar(
            intl.formatMessage({
              id: 'budget.updateBudgetSettingsSnackbarSuccess',
            }),
            {
              variant: 'success',
            },
          )
        }
      },
    },
  )

  const toggleIsDisabled = (isDisabled: any) => {
    setIsDisabled(!isDisabled);
  };

  return (
    <>
      <FormHandler
        title={intl.formatMessage({
          id: 'budget.manageBudget.formHandlerTitle',
        })}
        descriptionCreateMode={intl.formatMessage({
          id: 'budget.manageBudget.description',
        })}
        requestUrl='updateBudgetSettings'
        detailsRequestId={get(dialog, 'data.id')}
        detailsRequestCallback={() => { }}
        data={dialog?.data}
        closeDialog={closeDialog}
        fetchRows={fetchBudgetSettings}
        initialValues={initialValues}
        validationSchema={validationSchema}
        hideActionButton
        hideHeader
      >
        {({ formik, isEditMode }) => (
          <ManageBudgetForm
            title={intl.formatMessage({
              id: 'budget.manageBudget.budgetFormTitle',
            })}
            formik={formik}
            updateBudgetSettingsMutation={updateBudgetSettingsMutation}
            isEditMode={isEditMode}
            spendingBudgetPerEmployee={spendingBudgetPerEmployee}
            setSpendingBudgetPerEmployee={setSpendingBudgetPerEmployee}
            receivingBudgetPerEmployee={receivingBudgetPerEmployee}
            setReceivingBudgetPerEmployee={setReceivingBudgetPerEmployee}
            budgetSettings={budgetSettings}
            demographics={demographics}
            demographicsIsLoading={demographicsIsLoading}
            // roles={roles}
            // rolesIsLoading={rolesIsLoading}
            toggleIsDisabled={toggleIsDisabled}
            isDisabled={isDisabled}
          />
        )}
      </FormHandler>
    </>
  )
}
